/**
 * 中国时区（Asia/Shanghai）日期工具
 * 请求参数中的「今天」以中国时区为准，与后端一致。
 */

const BUSINESS_TZ = 'Asia/Shanghai';

/**
 * 将 Date 格式化为中国时区的 YYYY-MM-DD
 */
export function formatDateInChina(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * 将 Unix 时间戳（秒）格式化为中国时区的 YYYY-MM-DD
 */
export function formatTimestampToChinaDate(timestampSeconds: number): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(timestampSeconds * 1000));
}

/**
 * 将 YYYY-MM-DD 解析为中国时区该日 00:00:00 的 Date
 */
export function parseChinaDate(dateStr: string): Date {
  return new Date(`${dateStr}T00:00:00+08:00`);
}

/**
 * 中国时区「今天」零点的 Unix 时间戳（秒），用于请求参数
 */
export function getTodayStartInChinaSeconds(): number {
  const todayStr = formatDateInChina(new Date());
  return Math.floor(new Date(`${todayStr}T00:00:00+08:00`).getTime() / 1000);
}

/**
 * 中国时区「上一营业日」的 YYYY-MM-DD（业务报告接口只支持到上一营业日，不支持当天）
 */
export function getYesterdayStrInChina(): string {
  return formatTimestampToChinaDate(getTodayStartInChinaSeconds() - 86400);
}

/**
 * 将 Date 格式化为中国时区的日期时间字符串（YYYY/M/D HH:mm:ss），用于展示
 */
export function formatDateTimeInChina(date: Date): string {
  const formatter = new Intl.DateTimeFormat('zh-CN', {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  return formatter.format(date).replaceAll(/\s/g, ' ');
}

/**
 * 获取中国时区下该时刻的小时数（0–23）
 */
export function getChinaHour(date: Date): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TZ,
    hour: 'numeric',
    hour12: false,
  }).formatToParts(date);
  return Number.parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
}

/**
 * 将 Unix 时间戳（秒）解析为中国时区的年/月/日，用于同期对齐（避免浏览器时区导致错位）
 */
export function getChinaDateParts(timestampSeconds: number): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(timestampSeconds * 1000));
  const year = Number.parseInt(parts.find((p) => p.type === 'year')?.value ?? '0', 10);
  const month = Number.parseInt(parts.find((p) => p.type === 'month')?.value ?? '0', 10) - 1; // 0-based
  const day = Number.parseInt(parts.find((p) => p.type === 'day')?.value ?? '0', 10);
  return { year, month, day };
}
